import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CheckboxComponent } from '../checkbox/checkbox.component';

export type DropdownVariant =
  | 'outlined'
  | 'primary'
  | 'naked-primary'
  | 'secondary'
  | 'naked-secondary'
  | 'destructive'
  | 'inverted-primary';

export type DropdownSize = 'small' | 'medium' | 'large';

export interface DropdownOption {
  id?: string | number;
  label: string;
  description?: string;
  value?: any;
  icon?: string;
  disabled?: boolean;
  destructive?: boolean;
  divider?: boolean;
  selected?: boolean;
}

@Component({
  selector: 'app-dropdown',
  standalone: true,
  imports: [CommonModule, CheckboxComponent],
  templateUrl: './dropdown.component.html',
})
export class DropdownComponent {
  @Input() variant: DropdownVariant = 'primary';
  @Input() size: DropdownSize = 'medium';
  @Input() disabled: boolean = false;
  @Input() label: string = '';
  @Input() badge: number | string | null = null;
  @Input() leftIcon: string = '';
  @Input() showCaret: boolean = true;
  @Input() align: 'left' | 'right' = 'left';
  @Input() options: DropdownOption[] = [];
  @Input() value: any = null;
  @Input() multiSelect: boolean = false;
  @Input() selectedValues: any[] = [];
  @Input() className: string = '';
  @Input() menuClassName: string = '';
  @Input() placeholder: string = 'Select option';

  @Input() isOpen: boolean = false;
  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() selectOption = new EventEmitter<DropdownOption>();
  @Output() selectedValuesChange = new EventEmitter<any[]>();

  private elementRef = inject(ElementRef);

  toggleDropdown(event?: MouseEvent): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    this.isOpenChange.emit(this.isOpen);
  }

  closeDropdown(): void {
    if (this.isOpen) {
      this.isOpen = false;
      this.isOpenChange.emit(false);
    }
  }

  isOptionSelected(option: DropdownOption): boolean {
    const val = option.value !== undefined ? option.value : (option.id !== undefined ? option.id : option.label);
    if (this.multiSelect) {
      if (Array.isArray(this.selectedValues)) {
        return this.selectedValues.includes(val);
      }
      if (Array.isArray(this.value)) {
        return this.value.includes(val);
      }
      return false;
    }
    return this.value === val;
  }

  get effectiveSelectedCount(): number {
    if (Array.isArray(this.selectedValues) && this.selectedValues.length > 0) {
      return this.selectedValues.length;
    }
    if (Array.isArray(this.value) && this.value.length > 0) {
      return this.value.length;
    }
    return 0;
  }

  clearSelection(event: MouseEvent): void {
    event.stopPropagation();
    this.selectedValues = [];
    this.selectedValuesChange.emit([]);
  }

  onSelect(option: DropdownOption, event?: MouseEvent): void {
    if (option.disabled || option.divider) return;
    if (event) {
      event.stopPropagation();
    }
    if (this.multiSelect) {
      const optionVal = option.value !== undefined ? option.value : (option.id !== undefined ? option.id : option.label);
      const current = Array.isArray(this.selectedValues)
        ? [...this.selectedValues]
        : (Array.isArray(this.value) ? [...this.value] : []);
      const index = current.indexOf(optionVal);
      const updated = index > -1
        ? current.filter((v) => v !== optionVal)
        : [...current, optionVal];
      this.selectedValues = updated;
      this.selectedValuesChange.emit(updated);
      this.selectOption.emit(option);
      return;
    }
    this.selectOption.emit(option);
    this.closeDropdown();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closeDropdown();
  }

  // Compute size-specific styles matching ButtonComponent
  get sizeClasses(): string {
    switch (this.size) {
      case 'large':
        return 'h-[48px] min-h-[48px] px-5 text-base gap-2.5';
      case 'small':
        return 'h-[36px] min-h-[36px] px-3 text-xs gap-1.5';
      case 'medium':
      default:
        return 'h-[40px] min-h-[40px] px-4 text-sm gap-2';
    }
  }

  // Compute variant-specific styles matching ButtonComponent
  get variantClasses(): string {
    if (this.disabled) {
      switch (this.variant) {
        case 'outlined':
          return 'bg-transparent border-button-outlined-border-disabled text-text-dimmed cursor-not-allowed';
        case 'primary':
          return 'bg-button-primary-disabled border-transparent text-text-dimmed cursor-not-allowed';
        case 'naked-primary':
          return 'bg-transparent border-transparent text-text-dimmed cursor-not-allowed';
        case 'secondary':
          return 'bg-button-secondary-disabled border-transparent text-text-dimmed cursor-not-allowed';
        case 'naked-secondary':
          return 'bg-transparent border-transparent text-text-dimmed cursor-not-allowed';
        case 'destructive':
          return 'bg-button-destructive-disabled border-transparent text-text-destructive-disaabled cursor-not-allowed';
        case 'inverted-primary':
          return 'bg-button-inverted-primary-disabled border-transparent text-text-dimmed cursor-not-allowed';
      }
    }

    switch (this.variant) {
      case 'outlined':
        return 'bg-transparent border-button-outlined-border-default hover:border-button-outlined-border-hover active:border-button-outlined-border-pressed text-text-primary';
      case 'primary':
        return 'bg-button-primary-default border-transparent hover:bg-button-primary-hover active:bg-button-primary-pressed text-text-inverted-primary';
      case 'naked-primary':
        return 'bg-transparent border-transparent hover:bg-button-naked-hover active:bg-button-naked-pressed text-text-primary';
      case 'secondary':
        return 'bg-button-secondary-default border-transparent hover:bg-button-secondary-hover active:bg-button-secondary-pressed text-text-primary';
      case 'naked-secondary':
        return 'bg-transparent border-transparent hover:bg-transparent text-text-half-dimmed hover:text-text-primary active:text-text-half-dimmed';
      case 'destructive':
        return 'bg-button-destructive-default border-transparent hover:bg-button-destructive-hover active:bg-button-destructive-pressed text-text-primary';
      case 'inverted-primary':
        return 'bg-button-inverted-primary-default border-transparent hover:bg-button-inverted-primary-hover active:bg-button-inverted-primary-pressed text-text-primary';
    }
  }
}
